/**
 * The in-list panel's state, held in the URL and nowhere else:
 * `?person=<handle>&tab=<profile|activity|sync>` on Import Detail.
 *
 * The search params are the single source of truth (PRD failure case "URL and
 * panel disagree"), so a reload, a copied link and the Back button all land on
 * the same view. Every write goes through the functional `setSearchParams`
 * form and touches ONLY `person` and `tab`, so whatever else the page keeps in
 * its query string (`run`, `preview`) survives opening, moving and closing.
 *
 * History:
 * - **open** (a click on an avatar) PUSHES, so Back closes the panel;
 * - **move** (‹ ›, ←/→) and **tab** REPLACE, so walking a list of 50 people
 *   does not leave 50 history entries between the reader and the page before;
 * - **close** after an open from this page GOES BACK (`navigate(-1)`) to the
 *   entry the open pushed from. Replacing there left `[list, list]` in the
 *   history, so the reader's next Back did nothing visible (/pr-review #954
 *   iter 1). The pushed entry is marked in its history state
 *   (`personPanelPushed`), and move / tab carry the mark along, so the check
 *   survives walking ‹ › and a reload.
 * - **close** of a panel nobody opened here (a deep link, a shared URL)
 *   REPLACES: there is no entry below it to go back to, and a pushed close
 *   would make Back re-open it.
 *
 * Invariant: exactly ONE marked entry per open panel. A second `open` while a
 * panel is up would push another marked entry, and one close would then land
 * on the previous person instead of closing; callers that switch person with
 * a panel already open use `move` (/pr-review #954 iter 2).
 *
 * History state is MERGED, never replaced: every write keeps whatever else the
 * page stored on the entry (`ImportDetailPage` reads `state.from` for its
 * back-to-list link) and only adds or removes `personPanelPushed`.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { type PersonTab, isPersonHandle, parsePersonTab } from "./personHandle";

export interface PersonPanelUrl {
  /** The open person, or null when the panel is closed (or `person=` is junk). */
  handle: string | null;
  tab: PersonTab;
  open: (handle: string, tab?: PersonTab) => void;
  move: (handle: string) => void;
  setTab: (tab: PersonTab) => void;
  close: () => void;
}

/** The history-state key marking the entry an in-page open pushed. */
const PUSHED_KEY = "personPanelPushed";

function asRecord(state: unknown): Record<string, unknown> {
  return typeof state === "object" && state !== null ? (state as Record<string, unknown>) : {};
}

function wasPushedHere(state: unknown): boolean {
  return asRecord(state)[PUSHED_KEY] === true;
}

/** The entry's state with the mark added, everything else kept. */
function withMark(state: unknown): Record<string, unknown> {
  return { ...asRecord(state), [PUSHED_KEY]: true };
}

/** The entry's state with the mark removed, everything else kept (null when
 *  nothing else is left, as a plain navigation would store). */
function withoutMark(state: unknown): Record<string, unknown> | null {
  const { [PUSHED_KEY]: _mark, ...rest } = asRecord(state);
  return Object.keys(rest).length > 0 ? rest : null;
}

export function usePersonPanelUrl(): PersonPanelUrl {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pushedHere = wasPushedHere(location.state);
  const rawHandle = searchParams.get("person");
  const handle = isPersonHandle(rawHandle) ? rawHandle : null;
  const tab = parsePersonTab(searchParams.get("tab"));

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void, replace: boolean, state: unknown) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace, state }
      );
    },
    [setSearchParams]
  );
  // What a replace keeps: the entry's whole state, the pushed mark included
  // while the panel stays open.
  const keptState: unknown = location.state ?? null;

  const open = useCallback(
    (h: string, t: PersonTab = "profile") =>
      update(
        (next) => {
          next.set("person", h);
          next.set("tab", t);
        },
        false,
        withMark(location.state)
      ),
    [update, location.state]
  );
  const move = useCallback(
    (h: string) =>
      update(
        (next) => {
          next.set("person", h);
          next.set("tab", parsePersonTab(next.get("tab")));
        },
        true,
        keptState
      ),
    [update, keptState]
  );
  const setTab = useCallback(
    (t: PersonTab) => update((next) => next.set("tab", t), true, keptState),
    [update, keptState]
  );
  const close = useCallback(() => {
    if (pushedHere) {
      navigate(-1);
      return;
    }
    update(
      (next) => {
        next.delete("person");
        next.delete("tab");
      },
      true,
      withoutMark(location.state)
    );
  }, [update, pushedHere, navigate, location.state]);

  return useMemo(
    () => ({ handle, tab, open, move, setTab, close }),
    [handle, tab, open, move, setTab, close]
  );
}
